import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { format, isValid, parseISO } from "date-fns";
import { Field, Form, Formik } from "formik";
import classNames from "classnames";
import { Tooltip } from "react-tooltip";

import Pagination from "../../../shared/components/Table/Pagination";
import { EmptyListPlaceholderContent } from "../../../shared/Ui/EmptyPageBlock";
import SelectCustomFormik from "../../../shared/Ui/SelectCustom/SelectCustomFormik";
import CalendarSelectFormik from "../../../shared/Ui/Calendar/CalendarSelectFormik";
import { toApiDate } from "../ParticipantsRegistry/participantsRegistryUtils";
import StatusBadge from "../../../shared/Ui/StatusBadge/StatusBadge";
import ActionDropwdownCell from "../../../shared/components/Table/ActionDropwdownCell";
import ModalComponent from "../../../shared/Ui/ModalComponent/ModalWindow";
import ModalButton from "../../../shared/Ui/Buttons/ModalButtons";
import {
  MarkingCodesReportItem,
  MarkingCodesReportStatus,
  downloadMarkingCodesReport,
  searchMarkingCodesReports,
} from "../../../common/api/markingCodesReportApi";
import { downloadPdfBlob, downloadXlsxBlob } from "../../Billing/billingUtils";
import { useAppSelector } from "../../../shared/hook";
import style from "../../EventsAudit/EventAuditList.module.css";

type PaginationState = {
  pageIndex: number;
  pageSize: number;
};

type FilterValues = {
  createdFrom: string;
  createdTo: string;
  reportNumber: string;
  status: string;
};

const EMPTY_FILTER: FilterValues = {
  createdFrom: "",
  createdTo: "",
  reportNumber: "",
  status: "",
};

const formatDisplayDateTime = (value?: string) => {
  if (!value) return "-";
  const parsed = parseISO(value);
  if (!isValid(parsed)) return value;
  return format(parsed, "dd.MM.yyyy HH:mm");
};

const getStatusMeta = (status: string, t: (key: string) => string) => {
  switch (status) {
    case "FORMING":
      return { type: "test", label: t("marking_codes_report.status_forming") };
    case "FORMED":
      return { type: "active", label: t("marking_codes_report.status_formed") };
    case "ERROR":
      return { type: "error", label: t("marking_codes_report.status_error") };
    default:
      return { type: "", label: status || "-" };
  }
};

function MarkingCodesStatusReportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pageSize = useAppSelector((state) => state.userSettings.pageSize);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize || 20,
  });
  const [tableData, setTableData] = useState({ totalItems: 0, totalPages: 0 });
  const [values, setValues] = useState<MarkingCodesReportItem[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filter, setFilter] = useState<FilterValues>(EMPTY_FILTER);
  const [activeTooltipId, setActiveTooltipId] = useState<number | null>(null);
  const [downloadModalValue, setDownloadModalValue] =
    useState<MarkingCodesReportItem | null>(null);

  const statusOptions = [
    { value: "FORMING", label: t("marking_codes_report.status_forming") },
    { value: "FORMED", label: t("marking_codes_report.status_formed") },
    { value: "ERROR", label: t("marking_codes_report.status_error") },
  ];

  const loadList = async (
    page = pagination.pageIndex + 1,
    filterOverride?: FilterValues,
  ) => {
    try {
      const current = filterOverride ?? filter;
      const response = await searchMarkingCodesReports(
        page,
        pagination.pageSize,
        {
          dateFrom: toApiDate(current.createdFrom),
          dateTo: toApiDate(current.createdTo),
          reportNumber: current.reportNumber.trim() || undefined,
          status: (current.status || undefined) as MarkingCodesReportStatus | undefined,
        },
      );
      const items = response?.reports ?? [];
      setValues(items);
      setTableData({
        totalItems: response?.totalItems ?? items.length,
        totalPages: response?.totalPages ?? 0,
      });
    } catch (error) {
      console.error("Error fetching marking codes reports:", error);
      setValues([]);
      setTableData({ totalItems: 0, totalPages: 0 });
    }
  };

  useEffect(() => {
    loadList();
  }, [pagination.pageIndex, pagination.pageSize]);

  const applyFilter = (next: FilterValues) => {
    setFilter(next);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setIsFilterOpen(false);
    loadList(1, next);
  };

  const handleDownload = async (value: MarkingCodesReportItem, fileFormat: "XLSX" | "PDF") => {
    if (!value.id) return;
    try {
      const blob = await downloadMarkingCodesReport(value.id, fileFormat);
      const baseName = value.reportNumber || `marking-codes-report-${value.id}`;
      if (fileFormat === "XLSX") {
        downloadXlsxBlob(blob, `${baseName}.xlsx`);
      } else {
        downloadPdfBlob(blob, `${baseName}.pdf`);
      }
    } catch (error) {
      console.error("Error downloading marking codes report:", error);
      window.alert(t("billing.exportError"));
    } finally {
      setDownloadModalValue(null);
    }
  };

  // Меню действий строки — как в журнале балансового отчёта
  const buildActions = (value: MarkingCodesReportItem) => {
    const actions: { title: string; action: string | (() => void) }[] = [
      { title: t("billing.view"), action: `/reports/marking-codes-status/${value.id}` },
    ];
    if (value.status === "FORMED") {
      actions.push({
        title: t("mainButtons.download"),
        action: () => setDownloadModalValue(value),
      });
    }
    return actions;
  };

  return (
    <div
      className="flex flex-1 flex-col min-w-0"
      onClick={() => isFilterOpen && setIsFilterOpen(false)}
    >
      <h2 className="my-3 text-xl font-semibold">
        {t("marking_codes_report.title")}
      </h2>

      <div className="bg-white my-5 p-5 pt-8 relative rounded-xl shadow-md flex flex-row items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          className="bg-green text-white font-semibold py-2 px-4 rounded"
          onClick={() => navigate("/reports/marking-codes-status/new")}
        >
          {t("marking_codes_report.create")}
        </button>
        <div className="flex flex-row gap-2 items-center">
          <button
            type="button"
            className="bg-green text-white font-semibold py-2 px-4 rounded"
            data-tooltip-id="marking-codes-report-filter"
            data-tooltip-offset={4}
            onClick={(e) => {
              e.stopPropagation();
              setIsFilterOpen(true);
            }}
          >
            {t("mainButtons3.filter")}
          </button>
          <button type="button" onClick={() => loadList()}>
            <img src="/icons/update.svg" alt="update" />
          </button>
        </div>
      </div>

      <div className="w-full">
        <div className="rounded-lg overflow-x-auto bg-white">
          <table className="max-w-full w-full leading-normal">
            <thead className="border-b-2 border-gray-300">
              <tr>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  №
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("marking_codes_report.report_number")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("marking_codes_report.generated_at")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("marking_codes_report.executor")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("marking_codes_report.status")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200" />
              </tr>
            </thead>
            {values.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={6} className="text-center py-5">
                    <EmptyListPlaceholderContent
                      title={t("marking_codes_report.no_data")}
                    />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="font-normal text-center">
                {values.map((value, index) => {
                  const st = getStatusMeta(value.status, t);
                  return (
                    <tr
                      key={`mc-report-${value.id ?? index}`}
                      className={
                        value.id ? "cursor-pointer hover:bg-gray-100" : ""
                      }
                      onClick={() => {
                        if (value.id) {
                          navigate(`/reports/marking-codes-status/${value.id}`);
                        }
                      }}
                    >
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {pagination.pageIndex * pagination.pageSize + index + 1}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {value.reportNumber || value.id || "-"}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {formatDisplayDateTime(
                          value.completedAt ||
                            value.requestedAt ||
                            value.startedAt,
                        )}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {value.requestedByName || "-"}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        <div className="flex justify-center">
                          <StatusBadge type={st.type}>{st.label}</StatusBadge>
                        </div>
                      </td>
                      <td
                        className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {value.id && (
                          <ActionDropwdownCell
                            id={value.id}
                            actions={buildActions(value)}
                            activeTooltipId={activeTooltipId}
                            onTooltipToggle={(id) =>
                              setActiveTooltipId((prev) => (prev === id ? null : id))
                            }
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        <Pagination
          totalItems={tableData.totalItems}
          totalPages={tableData.totalPages}
          pagination={pagination}
          setPagination={setPagination}
        />
      </div>

      <div className="z-[1000] relative" onClick={(e) => e.stopPropagation()}>
        <Tooltip
          isOpen={isFilterOpen}
          id="marking-codes-report-filter"
          className={classNames(style.wrapperTooltip, "drop-shadow-2xl")}
          openOnClick
          clickable
          noArrow
          opacity={1}
          place="bottom-end"
          afterShow={() => setIsFilterOpen(true)}
          afterHide={() => setIsFilterOpen(false)}
        >
          <Formik initialValues={filter} enableReinitialize onSubmit={applyFilter}>
            {(formik) => (
              <Form className="min-w-[320px] p-2">
                <div className="flex flex-row justify-between items-center mb-2">
                  <p className="text-lg font-semibold">
                    {t("documents_upload.filters")}
                  </p>
                  <button type="button" onClick={() => setIsFilterOpen(false)}>
                    <img src="/icons/close.svg" alt="" />
                  </button>
                </div>
                <div className={style.inputField}>
                  <label className={style.inputLabel}>
                    {t("marking_codes_report.generated_at")}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <CalendarSelectFormik
                      name="createdFrom"
                      documentStatus="new"
                      outputFormat="dd.MM.yyyy"
                      placeholder={t("participants_registry_report.date_from_ph")}
                    />
                    <CalendarSelectFormik
                      name="createdTo"
                      documentStatus="new"
                      outputFormat="dd.MM.yyyy"
                      placeholder={t("participants_registry_report.date_to_ph")}
                    />
                  </div>
                </div>
                <div className={style.inputField}>
                  <label className={style.inputLabel}>
                    {t("marking_codes_report.report_number")}
                  </label>
                  <Field
                    type="text"
                    name="reportNumber"
                    className={style.placeholder}
                    placeholder="REP03-..."
                  />
                </div>
                <div className={style.inputField}>
                  <label className={style.inputLabel}>
                    {t("marking_codes_report.status")}
                  </label>
                  <Field
                    component={SelectCustomFormik}
                    name="status"
                    isClearable
                    isMulti={false}
                    size="dm"
                    options={statusOptions}
                    placeholder={t("documentLabels.choose")}
                    onChange={(next: string) => formik.setFieldValue("status", next || "")}
                  />
                </div>
                <div className="flex flex-row gap-2 mt-3">
                  <button
                    type="submit"
                    className="bg-green text-white font-semibold py-2 px-4 rounded"
                  >
                    {t("mainButtons3.apply")}
                  </button>
                  <button
                    type="button"
                    className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
                    onClick={() => applyFilter(EMPTY_FILTER)}
                  >
                    {t("mainButtons3.reset")}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </Tooltip>
      </div>

      <ModalComponent
        isOpen={Boolean(downloadModalValue)}
        onClose={() => setDownloadModalValue(null)}
        headerContent={t("orderList2.downloadFile")}
        content={
          downloadModalValue && (
            <div className="flex flex-col space-y-2">
              <button
                type="button"
                className="bg-green text-white font-semibold py-2 px-4 rounded hover:opacity-90 transition duration-300"
                onClick={() => handleDownload(downloadModalValue, "XLSX")}
              >
                {t("mainButtons.download")} Excel
              </button>
              <button
                type="button"
                className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:opacity-90 transition duration-300"
                onClick={() => handleDownload(downloadModalValue, "PDF")}
              >
                {t("mainButtons.download")} PDF
              </button>
            </div>
          )
        }
        customContent={
          <ModalButton
            type="button"
            color="grey"
            onClick={() => setDownloadModalValue(null)}
          >
            {t("mainButtons.close")}
          </ModalButton>
        }
      />
    </div>
  );
}

export default MarkingCodesStatusReportPage;
